import { isArray } from 'lodash'
import { normalizeComplianceAuditCellValue } from '@augmentt-dev/ui-components/src/reports/ComplianceAudit/utils'
import {
  getPostureRationale,
  getPostureStatusDetails,
} from '@augmentt-dev/ui-components/src/reports/SecurityPosture/utils'
import sortBy from './sortBy'
import {
  formatBlockLegacyAuthenticationReportData,
  formatBlockSignInSharedMailboxesReportData,
  formatInactiveAccountsReportData,
  formatPowershellAccessForNonAdminsReportData,
  formatSelfServePasswordResetReportData,
} from './postureCheckExportFormatters'
import {
  employeeNameSort,
  formatConditionalAccessPolicyCount,
  formatMfaStatusType,
  getAuthenticationTypeFormatter,
} from './table.util'

export const getMfaConfiguration = (item, intl) => {
  const configurationStatus = item?.mfaConfigurations?.configurationStatus
  const numberCaPolicies = item?.mfaConfigurations?.affectedByCAs?.length

  const configurationStatusMessage = configurationStatus
    ? intl.formatMessage({ id: `INTEGRATION.${configurationStatus}` })
    : ''
  const isAffectedByCAs = ['CAP', 'DUO', 'DUO_AND_CAP'].includes(configurationStatus)
  const numberCaPoliciesMessage = formatConditionalAccessPolicyCount(numberCaPolicies, intl)

  return `${configurationStatusMessage} ${isAffectedByCAs ? '/' : ''} ${
    isAffectedByCAs ? numberCaPoliciesMessage : ''
  }`.trim()
}

export const getMfaStatus = (item, intl) => {
  const mfaStatus = item?.mfaStatus?.status
  const mfaStatusMessage = mfaStatus ? intl.formatMessage({ id: `INTEGRATION.${mfaStatus}` }) : ''

  const statusList = item.mfaStatus?.statusList ?? []
  const statusListMessage = statusList
    .map((obj) =>
      `${formatMfaStatusType(obj.type, intl)} ${obj.status ? '-' : ''} ${
        obj.status ? intl.formatMessage({ id: `GENERAL.${obj.status}` }) : ''
      }`.trim(),
    )
    .join(', ')

  return `${mfaStatusMessage} ${statusListMessage ? '/' : ''} ${statusListMessage}`.trim()
}

const formatMfaReportData = ({ employees }, { order }, intl) =>
  employees
    .sort((a, b) => employeeNameSort(a, b, order, undefined, a, b))
    .map(({ email, licenseType, role, ...item }) => ({
      name: item.displayName || `${item.firstName} ${item.lastName}`,
      email,
      licenseType,
      role,
      mfaConfiguration: getMfaConfiguration(item, intl),
      mfaStatus: getMfaStatus(item, intl),
      authenticationType: item.authenticationType?.map(getAuthenticationTypeFormatter(intl)).join(', '),
    }))

const formatLicenseReportData = ({ users }, { order }) => {
  const sortedUsers = order ? sortBy(users, { field: 'name', order }) : users

  return sortedUsers.map(({ email, roles, licenses, change, status, name }) => ({
    employee: name,
    email,
    role: roles.join(','),
    licenses: licenses.join(','),
    change: change.join(','),
    status,
  }))
}

const formatComplianceAuditNoteStatus = (note, intl) => {
  if (!note) {
    return ''
  }
  if (note.status === 'custom' && note.customStatus) {
    return note.customStatus
  }
  const messageId = {
    risk_accepted: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.RISK_ACCEPTED',
    planned: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.PLANNED',
    comment: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.COMMENT',
    custom: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.CUSTOM',
  }[note.status]

  if (messageId && intl) {
    return intl.formatMessage({ id: messageId, defaultMessage: note.status })
  }

  return note.status || ''
}

const formatComplianceAuditNotes = (notes, notesDisplayMode, intl) => {
  if (!Array.isArray(notes) || !notes.length) {
    return ''
  }

  const latest = [...notes].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
  if (!latest) {
    return ''
  }

  if (notesDisplayMode === 'statusOnly') {
    return formatComplianceAuditNoteStatus(latest, intl)
  }

  return latest.note ?? ''
}

const formatComplianceAuditStatusDetails = (row, intl) => {
  if (!intl) {
    return normalizeComplianceAuditCellValue(row.formattedStatusDetails, null)
  }

  // getPostureStatusDetails often returns FormattedMessage nodes; SheetJS cannot stringify
  // them, which left Status Details blank for every non-custom-status row.
  return normalizeComplianceAuditCellValue(getPostureStatusDetails(row, intl), intl)
}

const formatComplianceAuditRationale = (row, intl) => {
  if (!intl) {
    return ''
  }

  return normalizeComplianceAuditCellValue(getPostureRationale(row, intl), intl)
}

/**
 * Builds CSV/XLSX rows for the compliance audit export.
 * Optional `columnConfig` (from exporter state) controls which fields are included.
 */
const formatComplianceAuditReportData = (data, sort, intl, columnConfig) => {
  const postureChecks = isArray(data) ? data : data.postureChecks
  const visibleColumnIds = columnConfig?.visibleColumnIds
  const notesDisplayMode = columnConfig?.notesDisplayMode ?? 'fullText'
  const include = (columnId) => !visibleColumnIds || visibleColumnIds.includes(columnId)

  return sortBy(postureChecks, sort).map((row) => {
    const { formattedName, formattedStatus, category, formattedTemplates, msSecureScore, license, notes } = row
    const exportRow = { formattedName, formattedStatus }

    if (include('statusDetails')) {
      exportRow.formattedStatusDetails = formatComplianceAuditStatusDetails(row, intl)
    }
    if (include('categories')) {
      exportRow.category = category
    }
    if (include('rationale')) {
      exportRow.rationale = formatComplianceAuditRationale(row, intl)
    }
    if (include('assessments')) {
      exportRow.formattedTemplates = Array.isArray(formattedTemplates) ? formattedTemplates.join(',') : ''
    }
    if (include('license')) {
      exportRow.license = license ?? ''
    }
    if (include('secureScore')) {
      exportRow.msSecureScoreCurrentScore = msSecureScore && parseFloat(msSecureScore.currentScore)
      exportRow.msSecureScoreMaxScore = msSecureScore && parseFloat(msSecureScore.maxScore)
    }
    if (include('notes')) {
      exportRow.notes = formatComplianceAuditNotes(notes, notesDisplayMode, intl)
    }

    return exportRow
  })
}

const formatPreformattedExportData = (data) => data

export default {
  MFA_REPORT: formatMfaReportData,
  LICENSE_REPORT: formatLicenseReportData,
  COMPLIANCE_AUDIT_REPORT: formatComplianceAuditReportData,
  BLOCK_LEGACY_AUTHENTICATION_REPORT: formatBlockLegacyAuthenticationReportData,
  BLOCK_SIGN_IN_SHARED_MAILBOXES_REPORT: formatBlockSignInSharedMailboxesReportData,
  INACTIVE_ACCOUNTS_REPORT: formatInactiveAccountsReportData,
  POWERSHELL_ACCESS_FOR_NON_ADMINS_REPORT: formatPowershellAccessForNonAdminsReportData,
  SELF_SERVE_PASSWORD_RESET_REPORT: formatSelfServePasswordResetReportData,
  COMPLIANCE_CHECK_ENTITY_REPORT: formatPreformattedExportData,
}
